import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function splitTextIntoSpans(container) {
    const textElements = container.querySelectorAll('.scrollytellingItems__text');

    textElements.forEach((textEl) => {
        const text = textEl.textContent;
        // Split by spaces but keep the space with the previous word
        const words = text.split(/(?<=\s)/);

        textEl.innerHTML = '';

        words.forEach((word) => {
            if (word.trim() === '') return;

            const span = document.createElement('span');
            span.textContent = word;
            textEl.appendChild(span);
        });
    });
}

/**
 * Preloads all frame images and returns them as an array
 * @param {string} basePath - Path to frames folder (e.g., '/media/video/frames/169/')
 * @param {number} frameCount - Total number of frames
 * @param {string} extension - File extension (default: 'jpg')
 * @returns {Promise<HTMLImageElement[]>}
 */
function preloadFrames(basePath, frameCount, extension = 'jpg') {
    const frames = [];
    const promises = [];

    for (let i = 1; i <= frameCount; i++) {
        const img = new Image();
        const frameNumber = String(i).padStart(4, '0');
        img.src = `${basePath}frame_${frameNumber}.${extension}`;

        const promise = new Promise((resolve, reject) => {
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load frame ${i}`));
        });

        frames.push(img);
        promises.push(promise);
    }

    return Promise.all(promises).then(() => frames);
}

/**
 * Creates and initializes a canvas element
 * @param {HTMLElement} container - Container to append canvas to
 * @returns {Object} - Canvas element and context
 */
function createCanvas(container) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.objectFit = 'cover';

    container.appendChild(canvas);

    return { canvas, ctx };
}

/**
 * Renders a frame to the canvas with cover behavior
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLImageElement} image
 */
function renderFrame(ctx, canvas, image) {
    if (!image || !image.complete) return;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const imgWidth = image.naturalWidth;
    const imgHeight = image.naturalHeight;

    // Calculate cover dimensions
    const imgRatio = imgWidth / imgHeight;
    const canvasRatio = canvasWidth / canvasHeight;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (canvasRatio > imgRatio) {
        // Canvas is wider than image ratio
        drawWidth = canvasWidth;
        drawHeight = canvasWidth / imgRatio;
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
    } else {
        // Canvas is taller than image ratio
        drawHeight = canvasHeight;
        drawWidth = canvasHeight * imgRatio;
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
    }

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
}

/**
 * Sets up canvas dimensions based on container size
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLElement} container
 */
function resizeCanvas(canvas, container, referenceImage) {
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    let width = rect.width;
    let height = rect.height;

    // Berechne Aspect Ratio aus dem Referenzbild
    if (referenceImage && referenceImage.naturalWidth && referenceImage.naturalHeight) {
        let aspectRatio = referenceImage.naturalWidth / referenceImage.naturalHeight;

        // Force 480x600 ratio for mobile
        if (window.innerWidth < 768) {
            aspectRatio = 480 / 600;
        }

        height = width / aspectRatio;
    }

    canvas.width = width * dpr;
    canvas.height = height * dpr;

    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
}

function initCanvasScrollytelling(videoContainer, framesPath, frameCount, extension) {
    splitTextIntoSpans(videoContainer);

    const stickyInner = videoContainer.querySelector('.scrollytellingVideoSticky__inner');
    const items = videoContainer.querySelectorAll('.scrollytellingItems__item');

    if (!stickyInner) {
        console.error('scrollytellingVideo: .scrollytellingVideoSticky__inner not found');
        return;
    }

    // Remove existing video if present
    const existingVideo = stickyInner.querySelector('video');
    if (existingVideo) {
        existingVideo.remove();
    }

    const { canvas, ctx } = createCanvas(stickyInner);

    let frames = [];
    let currentFrameIndex = 0;

    // Handle resize
    const handleResize = () => {
        resizeCanvas(canvas, stickyInner, frames[0]);
        if (frames[currentFrameIndex]) {
            renderFrame(ctx, canvas, frames[currentFrameIndex]);
        }
    };

    window.addEventListener('resize', handleResize);

    // Preload frames and initialize
    preloadFrames(framesPath, frameCount, extension)
        .then((loadedFrames) => {
            frames = loadedFrames;

            // Resize canvas mit korrekter Aspect Ratio vom Frame
            resizeCanvas(canvas, stickyInner, frames[0]);

            // Render first frame
            renderFrame(ctx, canvas, frames[0]);

            const viewportHeight = window.innerHeight;

            // Frame scrubbing via ScrollTrigger
            ScrollTrigger.create({
                trigger: videoContainer,
                start: 'top top',
                end: 'bottom bottom',
                scrub: true,
                onUpdate: (self) => {
                    const frameIndex = Math.min(
                        Math.floor(self.progress * (frames.length - 1)),
                        frames.length - 1
                    );

                    if (frameIndex !== currentFrameIndex) {
                        currentFrameIndex = frameIndex;
                        renderFrame(ctx, canvas, frames[currentFrameIndex]);
                    }
                }
            });

            // Item positioning and text animations
            initItemAnimations(videoContainer, items, viewportHeight);
        })
        .catch((error) => {
            console.error('scrollytellingVideo: Failed to load frames', error);
        });
}

function initVideoScrollytelling(videoContainer) {
    splitTextIntoSpans(videoContainer);

    const videoElement = videoContainer.querySelector('video');
    const items = videoContainer.querySelectorAll('.scrollytellingItems__item');

    if (!videoElement) {
        return;
    }

    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.autoplay = false;
    videoElement.pause();

    let scrollTriggersInitialized = false;

    const once = (el, event, fn) => {
        const onceFn = function(e) {
            el.removeEventListener(event, onceFn);
            fn.apply(this, arguments);
        };
        el.addEventListener(event, onceFn);
        return onceFn;
    };

    // iOS Video Aktivierung
    once(document.documentElement, 'touchstart', () => {
        videoElement.play();
        videoElement.pause();
    });

    const initScrollTrigger = () => {
        if (scrollTriggersInitialized) {
            return;
        }

        const videoDuration = videoElement.duration;
        if (!videoDuration || isNaN(videoDuration) || videoDuration === 0) {
            return;
        }

        const viewportHeight = window.innerHeight;

        scrollTriggersInitialized = true;

        // Video scrubbing timeline
        const tl = gsap.timeline({
            defaults: { duration: 1 },
            scrollTrigger: {
                trigger: videoContainer,
                start: 'top top',
                end: 'bottom bottom',
                scrub: true
            }
        });

        tl.fromTo(
            videoElement,
            { currentTime: 0 },
            { currentTime: videoDuration }
        );

        // Item positioning and text animations
        initItemAnimations(videoContainer, items, viewportHeight);
    };

    // Initialisierung erst wenn Video komplett geladen ist
    if (videoElement.readyState >= 4) {
        initScrollTrigger();
    } else {
        videoElement.addEventListener('canplaythrough', function (){
            initScrollTrigger();
        });
    }

    if (videoElement.readyState === 0) {
        videoElement.load();
    }
}

function initItemAnimations(videoContainer, items, viewportHeight) {
    // Verschiedene Positionen für die Items
    // Mobile: immer unten, volle Breite
    let positions = [
        { top: 'auto', left: '0', right: '0', bottom: '40px', textAlign: 'center' }
    ];

    if(window.innerWidth > 768) {
        // Berechne den Offset für max 1920px Breite
        const maxWidth = 1920;
        const offset = window.innerWidth > maxWidth
            ? `calc((100vw - ${maxWidth}px) / 2 + 4rem)`
            : '4rem';

        positions = [
            { top: 'auto', left: offset, right: offset, bottom: '6rem', textAlign: 'center' }
        ];
    }

    items.forEach((item, index) => {
        const isLastItem = index === items.length - 1;
        const baseDuration = viewportHeight * 1.6; // zuvor 1.0 * viewportHeight
        const lastDuration = viewportHeight * 2.4; // zuvor 1.5 * viewportHeight

        // Erstes Item startet bei 50vh, dann jeweils baseDuration Abstand
        const startPx = index === 0 ? viewportHeight * 0.5 : (index * baseDuration) + (viewportHeight * 0.5);

        // End-Position berechnen, letztes Item bekommt mehr Zeit
        const endPx = isLastItem
            ? startPx + lastDuration
            : startPx + baseDuration;

        // Position aus Array holen (wiederholt sich nach 6 Items)
        const pos = positions[index % positions.length];

        // Position setzen
        gsap.set(item, {
            top: pos.top,
            left: pos.left,
            right: pos.right,
            bottom: pos.bottom,
            xPercent: pos.centerX ? -50 : 0
        });

        // Text-Ausrichtung als Klasse hinzufügen
        const textEl = item.querySelector('.scrollytellingItems__text');
        if (textEl) {
            textEl.classList.add(`is-${pos.textAlign}`);
        }

        // Spans im Item für staggered Animation
        const spans = item.querySelectorAll('.scrollytellingItems__text span');

        // ScrollTrigger für Span-Animation (CSS-Klassen basiert)
        if (spans.length > 0) {
            let animationTimeouts = [];

            const clearAllTimeouts = () => {
                animationTimeouts.forEach(id => clearTimeout(id));
                animationTimeouts = [];
            };

            const showSpans = () => {
                clearAllTimeouts();
                spans.forEach((span, i) => {
                    const timeoutId = setTimeout(() => {
                        span.classList.add('is-visible');
                        const revealId = setTimeout(() => {
                            span.classList.add('is-revealed');
                        }, 200);
                        animationTimeouts.push(revealId);
                    }, i * 70);
                    animationTimeouts.push(timeoutId);
                });
            };

            const hideSpans = () => {
                clearAllTimeouts();
                spans.forEach(span => {
                    span.classList.remove('is-visible', 'is-revealed');
                });
            };

            ScrollTrigger.create({
                trigger: videoContainer,
                start: `top top-=${startPx}px`,
                end: `top top-=${endPx}px`,
                onEnter: showSpans,
                onEnterBack: showSpans,
                onLeave: () => {
                    if (!isLastItem) {
                        hideSpans();
                    }
                },
                onLeaveBack: hideSpans
            });
        }
    });

    // Erstes Item: von fixed zu absolute wechseln am Anfang des Containers
    const firstItem = items[0];
    if (firstItem) {
        ScrollTrigger.create({
            trigger: videoContainer,
            start: 'top top',
            end: 'bottom bottom',
            onLeaveBack: () => {
                firstItem.style.position = 'absolute';
            },
            onEnter: () => {
                firstItem.style.position = 'fixed';
            }
        });
    }

    // Letztes Item: von fixed zu absolute wechseln am Ende des Containers
    const lastItem = items[items.length - 1];
    if (lastItem) {
        ScrollTrigger.create({
            trigger: videoContainer,
            start: 'bottom bottom',
            end: 'bottom top',
            onEnter: () => {
                lastItem.style.position = 'absolute';
            },
            onLeaveBack: () => {
                lastItem.style.position = 'fixed';
            }
        });
    }

    // Globaler ScrollTrigger um alle Items auszublenden wenn Container verlassen wird
    ScrollTrigger.create({
        trigger: videoContainer,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => {
            items.forEach(item => {
                item.style.visibility = 'visible';
            });
        },
        onEnterBack: () => {
            items.forEach(item => {
                item.style.visibility = 'visible';
            });
        },
        onLeave: () => {
            items.forEach(item => {
                item.style.visibility = 'hidden';
            });
        },
        onLeaveBack: () => {
            items.forEach(item => {
                item.style.visibility = 'hidden';
            });
        }
    });
}

function initHeadlineAnimation(videoContainer) {
    const wrapper = videoContainer.closest('.scrollytellingWrapper');
    if (!wrapper) return;

    const headline = wrapper.querySelector('.scrollytellingWrapper__headline');
    if (!headline) return;

    gsap.set(headline, { top: 0 });

    ScrollTrigger.create({
        trigger: videoContainer,
        start: 'top top',
        end: 'bottom top+=100%',
        pin: headline,
        pinSpacing: false,
        anticipatePin: 1,
        onUpdate: () => {
            // Force top to 0 when pinned
            if (headline.style.position === 'fixed') {
                headline.style.top = '0px';
            }
        }
    });
}

export function initScrollytellingVideo() {
    const videoContainers = document.querySelectorAll('.scrollytellingVideo');

    if (!videoContainers.length) {
        return;
    }

    videoContainers.forEach((videoContainer) => {
        // Headline pinning
        initHeadlineAnimation(videoContainer);

        // Check for canvas mode via data attributes
        let framesPath = videoContainer.dataset.framesPath + "desktop/";
        const frameCount = parseInt(videoContainer.dataset.frameCount, 10);
        const extension = videoContainer.dataset.frameExtension || 'jpg';

        if(window.outerWidth < 768){
            framesPath = videoContainer.dataset.framesPath + 'mobile/';
        }

        if (framesPath && frameCount) {
            // Canvas-based scrollytelling
            initCanvasScrollytelling(videoContainer, framesPath, frameCount, extension);
        } else {
            // Fallback to video-based scrollytelling
            initVideoScrollytelling(videoContainer);
        }
    });
}
